/**
 * PlayerVisuals — character animation helpers.
 *
 * The squash-and-stretch curve is v1's, unchanged, because it is the single
 * cheapest thing in the whole renderer that makes a jump feel like a jump.
 *
 * ── The two v1 bugs this file exists to fix ────────────────────────────────
 *
 * `crownMesh` was a MODULE-LEVEL SINGLETON (v1 PlayerVisuals.js:119). With one
 * player that reads as working; with eight it means only one player can ever
 * wear the crown, and giving it to a second player silently steals it from the
 * first. `idleTime` (v1 :105) had the same shape — one accumulator shared by
 * every caller, so eight characters bobbed in lockstep off whichever one was
 * updated last.
 *
 * Both are now per-mesh, stored in `mesh.userData`. There is no module state in
 * this file at all, which is the property that makes it correct for N players.
 */

import * as THREE from 'three/webgpu';
import { CHARACTERS } from '@chaos/protocol';
import { createAccentMaterial } from './ToonMaterials.js';
import { frame } from './state.js';

const REST_SCALE = new THREE.Vector3(1, 1, 1);

/**
 * How much a body gives when it is hit or fires, by archetype.
 *
 * Read off the roster's `archetype` — the one gameplay fact that says what a
 * character IS — so the tank squashes and staggers less than the assassin
 * without this file learning any number the sim owns. Pure presentation
 * multipliers: 1 is the reference feel.
 *
 *   squash   the on-hit squash (1 + 0.09·s wide, 1 − 0.11·s tall, decays 12/s)
 *   recoil   the body pitch kicked back by a cast (0.2 rad · recoil, decays 14/s)
 *   flinch   the arms-flung, spine-arched flinch pose amplitude
 */
const JUICE = {
  assassin: { squash: 1.25, recoil: 1.15, flinch: 1.2 },
  juggernaut: { squash: 0.55, recoil: 0.6, flinch: 0.5 },
  controller: { squash: 1.0, recoil: 1.0, flinch: 1.0 },
  trickster: { squash: 1.15, recoil: 1.05, flinch: 1.1 },
  artillery: { squash: 0.95, recoil: 1.1, flinch: 0.9 },
};
const JUICE_DEFAULT = { squash: 1, recoil: 1, flinch: 1 };

/** The juice profile for a character id (or a mesh's `userData.characterId`). */
export function juiceFor(characterId) {
  return JUICE[CHARACTERS[characterId]?.archetype] ?? JUICE_DEFAULT;
}

/** A cast's recoil kick relaxes at this rate (per second), on both character drivers. */
export const RECOIL_DECAY = 14;
/** The defeated body spins and shrinks to nothing over this long. */
const DEATH_SHRINK_S = 0.32;
const DEATH_SPIN = 24;

/**
 * The defeat: `obj` spins and shrinks to nothing over DEATH_SHRINK_S, and
 * fx.defeat's burst takes over from there. `deadT` is seconds since death.
 */
export function deathSpinShrink(obj, deadT, dt) {
  const k = Math.min(1, deadT / DEATH_SHRINK_S);
  obj.scale.setScalar(Math.max(0.001, 1 - k));
  obj.rotation.y += dt * DEATH_SPIN;
}

/** How fast the on-hit squash relaxes, per second. */
const HIT_SQUASH_DECAY = 12;

/**
 * Squash a body because it was just hit. Pairs with the white flash: the flash
 * says WHO, the squash says it HURT. Weighted by archetype.
 */
export function applyHitSquash(mesh) {
  if (!mesh) return;
  mesh.userData.hitSquash = juiceFor(mesh.userData.characterId).squash;
}

/** Multiplies the hit squash into a target scale and decays it. */
function applyHitSquashTo(ud, target) {
  const s = ud.hitSquash ?? 0;
  if (s <= 0.01) {
    ud.hitSquash = 0;
    return 0;
  }
  target.x *= 1 + 0.09 * s;
  target.y *= 1 - 0.11 * s;
  target.z *= 1 + 0.09 * s;
  ud.hitSquash = s * Math.exp(-HIT_SQUASH_DECAY * frame.delta);
  return s;
}
/** Scratch. Safe to share because it never survives a single call. */
const _targetScale = new THREE.Vector3(1, 1, 1);

/**
 * Rising and falling read differently: stretch on the way up, squash on the way
 * down, and hold rest in the dead band between so walking does not wobble.
 *
 * Landing gets its own impulse. Without it the squash just lerps back to rest
 * as vy hits zero and the impact frame — the one that sells weight — is the
 * softest frame of the whole fall. The peak fall speed is LATCHED because both
 * callers smear a landing across several decelerating frames (snapshot
 * interpolation remotely, the predictor's ground resolve locally), so the frame
 * that reads grounded never sees the pre-impact velocity directly.
 */
export function updateSquashStretch(mesh, velocityY, bodyScale = 1) {
  if (!mesh) return;
  const ud = mesh.userData;
  const now = performance.now();

  if (velocityY < -12) ud.fallPeakVy = Math.min(ud.fallPeakVy ?? 0, velocityY);
  if (velocityY >= -1 && (ud.fallPeakVy ?? 0) < -12) {
    // -45 is a hard fall; harder ones clamp rather than flatten to a pancake.
    ud.landSquash = Math.min(1, -ud.fallPeakVy / 45);
    ud.landDur = 140 + ud.landSquash * 90;
    ud.landUntil = now + ud.landDur;
    ud.fallPeakVy = 0;
  }

  if (now < (ud.landUntil ?? 0)) {
    // Snap in hard, ease out with the timer.
    const s = ud.landSquash * ((ud.landUntil - now) / ud.landDur);
    _targetScale.set(1 + s * 0.45, 1 - s * 0.5, 1 + s * 0.45);
    applyHitSquashTo(ud, _targetScale);
    // Body scale MULTIPLIES the squash rather than replacing it, and it has to
    // be applied on this early-return path too — otherwise the landing frame is
    // the one frame where a giant snaps back to normal size.
    _targetScale.multiplyScalar(bodyScale);
    mesh.scale.lerp(_targetScale, 0.5);
    return;
  }

  if (velocityY > 5) {
    const t = Math.min(velocityY / 25, 0.25);
    _targetScale.set(1 - t * 0.5, 1 + t, 1 - t * 0.5);
  } else if (velocityY < -10) {
    const t = Math.min(Math.abs(velocityY) / 50, 0.2);
    _targetScale.set(1 + t * 0.5, 1 - t, 1 + t * 0.5);
  } else {
    _targetScale.copy(REST_SCALE);
  }

  const hit = applyHitSquashTo(ud, _targetScale);
  _targetScale.multiplyScalar(bodyScale);
  // A hit snaps in (the impact frame is the one that sells it) and relaxes out.
  mesh.scale.lerp(_targetScale, hit > 0.3 ? 0.6 : 0.15);
}

function buildCrown() {
  const crown = new THREE.Group();
  const mat = createAccentMaterial(0xffd700);

  const base = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.35, 0.15, 8), mat);
  crown.add(base);

  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2;
    const point = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.2, 4), mat);
    point.position.set(Math.cos(angle) * 0.25, 0.17, Math.sin(angle) * 0.25);
    crown.add(point);
  }

  crown.position.y = 1.25;
  crown.userData.noOutline = true;
  return crown;
}

/**
 * Marks the current threat — most kills, or the last two standing.
 *
 * Per-instance. Eight simultaneous callers each own their own crown.
 */
export function updateLeaderCrown(playerMesh, isLeader) {
  if (!playerMesh) return;
  const existing = playerMesh.userData.crownMesh ?? null;

  if (isLeader && !existing) {
    const crown = buildCrown();
    // Procedural bodies vary in head height per seed and say where a crown
    // sits; the fixed 1.25 stays as the fallback for anything that doesn't.
    crown.position.y = playerMesh.userData.crownY ?? crown.position.y;
    playerMesh.add(crown);
    playerMesh.userData.crownMesh = crown;
    return;
  }

  if (!isLeader && existing) {
    playerMesh.remove(existing);
    for (const child of existing.children) child.geometry.dispose();
    existing.children[0]?.material.dispose();
    playerMesh.userData.crownMesh = null;
  }
}

/**
 * Cast anticipation: a brief crouch during an ability's wind-up so the telegraph
 * on the ground has a matching pose on the body.
 */
export function applyCastAnticipation(mesh, windupMs) {
  if (!mesh || windupMs <= 0) return;
  mesh.userData.castUntil = performance.now() + windupMs;
}

export function updateCastPose(mesh, now) {
  if (!mesh) return;
  const until = mesh.userData.castUntil ?? 0;
  if (now >= until) return;
  // Squash slightly and lean into the shot. Blends with squash-stretch because
  // both write the same scale and the last writer this frame is this one.
  mesh.scale.y *= 0.94;
  mesh.scale.x *= 1.03;
  mesh.scale.z *= 1.03;
}
